// PATH: src/pages/StudentImportPage.tsx — import d'élèves depuis un fichier Excel/CSV
//
// Trois temps : dépôt du fichier → correspondance des colonnes (devinée par le
// backend, corrigeable ici) → rapport d'exécution. Rien n'est écrit tant que la
// correspondance n'est pas confirmée : l'aperçu montre exactement ce qui sera créé.
import React, { useCallback, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Result,
  Select,
  Space,
  Steps,
  Table,
  Tag,
  Typography,
  Upload,
  message,
} from "antd";
import { DownloadOutlined, InboxOutlined, UploadOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useSchoolApi } from "../api/school";
import PageHeader from "../components/PageHeader";
import { FONT_MONO } from "../theme";
import type { ImportPreview, ImportResult, ImportResultRow, ImportRow } from "../models/school";

// Doit rester aligne sur MotDePasseProvisoire (tc_tilmischool_be,
// internal/service/sv_school/import_students.go). Affiche ici pour que la
// direction puisse le communiquer aux eleves : il n'est plus imprime par eleve
// dans le rapport, puisqu'il est le meme pour tous.
const MOT_DE_PASSE_PROVISOIRE = "azerty123";

const GROUP_LABELS: Record<string, string> = {
  eleve: "Élève",
  tuteur: "Tuteur principal",
  tuteur2: "Second tuteur",
  compte: "Compte de connexion",
};

const STATUS_TAGS: Record<string, { color: string; label: string }> = {
  CREE: { color: "success", label: "Créé" },
  PARTIEL: { color: "warning", label: "Fiche seule" },
  IGNORE: { color: "default", label: "Ignoré" },
  ERREUR: { color: "error", label: "Erreur" },
};

// downloadFile : même mécanique que l'export CSV de la page Administration —
// le fichier vient d'une requête authentifiée, pas d'un lien direct.
const downloadFile = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

// escCsv / buildCsv : point-virgule et BOM, pour qu'Excel en locale FR ouvre le
// fichier d'identifiants sans écran d'importation ni accents cassés.
const escCsv = (v: unknown): string => {
  const s = v === null || v === undefined ? "" : String(v);
  return /[";\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};
const buildCsv = (headers: string[], rows: string[][]): string =>
  "﻿" + [headers, ...rows].map((r) => r.map(escCsv).join(";")).join("\r\n");

// columnLetter : repère de colonne façon tableur (A, B, … Z, AA), pour que la
// correspondance se relise en regard du fichier ouvert dans Excel.
const columnLetter = (index: number): string => {
  let n = index;
  let out = "";
  do {
    out = String.fromCharCode(65 + (n % 26)) + out;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return out;
};

const StudentImportPage: React.FC = () => {
  const api = useSchoolApi();
  const navigate = useNavigate();

  const [step, setStep] = useState(0);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [mapping, setMapping] = useState<Record<string, number>>({});
  
  const [result, setResult] = useState<ImportResult | null>(null);
  const [busy, setBusy] = useState(false);

  // analyze relance systématiquement l'analyse côté serveur : les erreurs de
  // ligne dépendent de la correspondance, les recalculer ici les ferait diverger
  // de ce que l'exécution appliquera réellement.
  const analyze = useCallback(
    async (target: File, withMapping?: Record<string, number>) => {
      setBusy(true);
      try {
        const data = await api.importPreview(target, withMapping);
        setPreview(data);
        setMapping(data.mapping ?? {});
        setStep(1);
      } catch (e: any) {
        message.error(e?.response?.data?.error ?? "Fichier illisible");
      } finally {
        setBusy(false);
      }
    },
    [api]
  );

  const onSelectFile = useCallback(
    (selected: File) => {
      setFile(selected);
      setResult(null);
      void analyze(selected);
    },
    [analyze]
  );

  const onMappingChange = useCallback(
    (fieldKey: string, columnIndex?: number) => {
      const next = { ...mapping };
      if (columnIndex === undefined) delete next[fieldKey];
      else next[fieldKey] = columnIndex;
      setMapping(next);
      if (file) void analyze(file, next);
    },
    [analyze, file, mapping]
  );

  const runImport = useCallback(async () => {
    if (!file) return;
    setBusy(true);
    try {
      // Toujours true : le backend crée les comptes systématiquement, le
      // paramètre n'est conservé que pour la compatibilité de signature.
      const data = await api.importStudents(file, mapping, true);
      setResult(data);
      setStep(2);
    } catch (e: any) {
      message.error(e?.response?.data?.error ?? "Import interrompu");
    } finally {
      setBusy(false);
    }
  }, [api, file, mapping]);

  const downloadTemplate = useCallback(() => {
    api
      .importTemplate()
      .then((blob) => downloadFile(blob, "modele_import_eleves.xlsx"))
      .catch(() => message.error("Modèle indisponible"));
  }, [api]);

  /**
   * Export des identifiants à distribuer aux familles.
   *
   * Deux destinataires par ligne, et c'est la raison d'être de ce fichier :
   * l'élève, et son PARENT. Sans les identifiants du parent, celui-ci n'a aucun
   * moyen d'accéder au compte de son enfant — il s'en crée un nouveau, et le
   * même élève se retrouve avec deux comptes.
   *
   * Les colonnes parent sont vides sur les lignes suivantes d'une fratrie : un
   * parent n'a qu'un compte, il n'y a rien de nouveau à lui transmettre.
   */
  const downloadCredentials = useCallback(() => {
    const rows = (result?.rows ?? []).filter((r) => r.login || r.guardian_login);
    const csv = buildCsv(
      [
        "Nom élève",
        "Prénom élève",
        "Identifiant élève",
        "Mot de passe élève",
        "Identifiant parent",
        "Mot de passe parent",
      ],
      rows.map((r) => [
        r.last_name,
        r.first_name,
        r.login ?? "",
        r.password ?? "(choisi dans le fichier)",
        r.guardian_login ?? "",
        r.guardian_password ?? "",
      ])
    );
    downloadFile(new Blob([csv], { type: "text/csv;charset=utf-8" }), "identifiants_familles.csv");
  }, [result]);

  const blocked = (preview?.missing_required?.length ?? 0) > 0;

  return (
    <div>
      <PageHeader
        title="Importer des élèves"
        subtitle="Fichier Excel (.xlsx) ou CSV — une ligne par élève"
        action={
          <Button icon={<DownloadOutlined />} onClick={downloadTemplate}>
            Télécharger le modèle
          </Button>
        }
      />

      <Steps
        current={step}
        style={{ marginBottom: 24 }}
        items={[{ title: "Fichier" }, { title: "Correspondance" }, { title: "Rapport" }]}
      />

      {step === 0 && (
        <Card>
          <Upload.Dragger
            accept=".csv,.xlsx,.xlsm,.txt"
            maxCount={1}
            showUploadList={false}
            disabled={busy}
            beforeUpload={(f) => {
              onSelectFile(f as unknown as File);
              return false; // l'envoi est piloté par la page, pas par antd
            }}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">Déposez le fichier ici, ou cliquez pour le choisir</p>
            <p className="ant-upload-hint">
              La première ligne doit contenir les intitulés des colonnes. Les noms usuels sont reconnus
              automatiquement (« Nom », « Prénom », « Code Massar », « Téléphone du tuteur »…) et
              restent corrigeables à l'étape suivante.
            </p>
          </Upload.Dragger>
        </Card>
      )}

      {step === 1 && preview && (
        <>
          {blocked && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message="Colonnes obligatoires à associer"
              description={`Indiquez quelle colonne du fichier correspond à : ${preview.missing_required?.join(
                ", "
              )}.`}
            />
          )}

          <Card title="Correspondance des colonnes" style={{ marginBottom: 16 }}>
            <Table
              rowKey="key"
              size="small"
              pagination={false}
              dataSource={preview.fields}
              columns={[
                {
                  title: "Champ TilmiSchool",
                  render: (_, f) => (
                    <Space size={6}>
                      <span>{f.label}</span>
                      {f.required && <Tag color="red">obligatoire</Tag>}
                      <Tag bordered={false}>{GROUP_LABELS[f.group] ?? f.group}</Tag>
                    </Space>
                  ),
                },
                {
                  title: "Colonne du fichier",
                  width: 320,
                  render: (_, f) => (
                    <Select
                      allowClear
                      style={{ width: 300 }}
                      placeholder="— non importé —"
                      value={mapping[f.key]}
                      onChange={(v) => onMappingChange(f.key, v)}
                      options={preview.columns.map((c, i) => ({
                        value: i,
                        label: `${columnLetter(i)} · ${c || "(sans titre)"}`,
                      }))}
                    />
                  ),
                },
              ]}
            />
          </Card>

          <Card
            title={
              <Space size={8}>
                <span>Aperçu</span>
                <Tag color="success">{preview.valid} à créer</Tag>
                {preview.duplicates > 0 && <Tag>{preview.duplicates} déjà présents</Tag>}
                {preview.invalid > 0 && <Tag color="error">{preview.invalid} en erreur</Tag>}
              </Space>
            }
          >
            <Table<ImportRow>
              rowKey="line"
              size="small"
              dataSource={preview.rows}
              pagination={{ pageSize: 20, showSizeChanger: false }}
              columns={[
                { title: "Ligne", dataIndex: "line", width: 70 },
                { title: "Nom", width: 160, render: (_, r) => r.values.last_name },
                { title: "Prénom", width: 160, render: (_, r) => r.values.first_name },
                {
                  title: "Massar",
                  width: 140,
                  render: (_, r) =>
                    r.values.massar_code ? (
                      <span style={{ fontFamily: FONT_MONO, fontSize: 13 }}>{r.values.massar_code}</span>
                    ) : (
                      <span style={{ color: "var(--app-faint)" }}>—</span>
                    ),
                },
                {
                  title: "Tuteur",
                  render: (_, r) =>
                    r.values.guardian_last_name ? (
                      `${r.values.guardian_last_name} ${r.values.guardian_first_name ?? ""} · ${
                        r.values.guardian_phone ?? ""
                      }${r.values.guardian2_last_name
                        ? ` / ${r.values.guardian2_last_name} ${r.values.guardian2_first_name ?? ""} · ${r.values.guardian2_phone ?? ""}`
                        : ""}`
                    ) : (
                      <span style={{ color: "var(--app-faint)" }}>—</span>
                    ),
                },
                {
                  title: "État",
                  width: 280,
                  render: (_, r) => {
                    if (r.errors?.length) return <Tag color="error">{r.errors.join(" ; ")}</Tag>;
                    if (r.duplicate) return <Tag>déjà enregistré — ignoré</Tag>;
                    if (r.warnings?.length) return <Tag color="warning">{r.warnings.join(" ; ")}</Tag>;
                    return <Tag color="success">à créer</Tag>;
                  },
                },
              ]}
            />

            <Space direction="vertical" size={12} style={{ marginTop: 16, width: "100%" }}>
              {/* La création des comptes n'est plus optionnelle : l'accès gratuit à
                  Tilmicode suppose que le compte existe dès l'import, et un élève
                  sans compte serait impossible à passer en premium. */}
              <Alert
                type="info"
                showIcon
                message="Un compte est créé pour chaque élève, et un pour son parent"
                description={
                  <>
                    Accès gratuit à Tilmicode et Tilmid+. Les identifiants figureront dans le
                    rapport, téléchargeable en fin d'import.
                    <br />
                    Tous les élèves reçoivent le même mot de passe provisoire{" "}
                    <Typography.Text code>{MOT_DE_PASSE_PROVISOIRE}</Typography.Text> et devront en
                    choisir un nouveau à leur première connexion.
                    <br />
                    <strong>Chaque parent reçoit un mot de passe qui lui est propre</strong>, à lui
                    transmettre personnellement : son compte donne accès au suivi et au paiement de
                    ses enfants. Un parent ayant plusieurs enfants n'a qu'un seul compte — les
                    colonnes « parent » ne sont donc renseignées qu'une fois par famille.
                    <br />
                    Sans ces identifiants, le parent créera de nouveaux comptes de son côté et son
                    enfant se retrouvera avec deux comptes distincts.
                  </>
                }
              />
              <Space>
                <Button onClick={() => setStep(0)}>Changer de fichier</Button>
                <Button
                  type="primary"
                  icon={<UploadOutlined />}
                  loading={busy}
                  disabled={blocked || preview.valid === 0}
                  onClick={runImport}
                >
                  Importer {preview.valid} élève{preview.valid > 1 ? "s" : ""}
                </Button>
              </Space>
            </Space>
          </Card>
        </>
      )}

      {step === 2 && result && (
        <>
          <Result
            status={result.failed > 0 ? "warning" : "success"}
            title={`${result.created} élève${result.created > 1 ? "s" : ""} importé${
              result.created > 1 ? "s" : ""
            }`}
            subTitle={[
              result.accounts_created > 0 ? `${result.accounts_created} compte(s) de connexion créé(s)` : "",
              result.skipped > 0 ? `${result.skipped} ligne(s) déjà enregistrée(s)` : "",
              result.failed > 0 ? `${result.failed} ligne(s) en erreur` : "",
            ]
              .filter(Boolean)
              .join(" · ")}
            extra={[
              <Button key="students" type="primary" onClick={() => navigate("/eleves")}>
                Voir les élèves
              </Button>,
              result.accounts_created > 0 ? (
                <Button key="creds" icon={<DownloadOutlined />} onClick={downloadCredentials}>
                  Télécharger les identifiants
                </Button>
              ) : null,
              <Button
                key="again"
                onClick={() => {
                  setStep(0);
                  setFile(null);
                  setPreview(null);
                  setResult(null);
                }}
              >
                Importer un autre fichier
              </Button>,
            ]}
          />

          <Card title="Détail par ligne">
            <Table<ImportResultRow>
              rowKey="line"
              size="small"
              dataSource={result.rows}
              pagination={{ pageSize: 25, showSizeChanger: false }}
              columns={[
                { title: "Ligne", dataIndex: "line", width: 70 },
                { title: "Nom", width: 160, dataIndex: "last_name" },
                { title: "Prénom", width: 160, dataIndex: "first_name" },
                {
                  title: "Statut",
                  width: 120,
                  render: (_, r) => {
                    const tag = STATUS_TAGS[r.status] ?? { color: "default", label: r.status };
                    return <Tag color={tag.color}>{tag.label}</Tag>;
                  },
                },
                {
                  title: "Identifiant",
                  width: 180,
                  render: (_, r) =>
                    r.login ? <span style={{ fontFamily: FONT_MONO, fontSize: 13 }}>{r.login}</span> : null,
                },
                {
                  title: "Mot de passe",
                  width: 140,
                  render: (_, r) =>
                    r.password ? <span style={{ fontFamily: FONT_MONO, fontSize: 13 }}>{r.password}</span> : null,
                },
                {
                  // Le compte du PARENT : c'est lui qui paie et suit ses
                  // enfants. Sans ces identifiants, il en recrée de nouveaux.
                  title: "Identifiant parent",
                  width: 180,
                  render: (_, r) =>
                    r.guardian_login ? (
                      <span style={{ fontFamily: FONT_MONO, fontSize: 13 }}>{r.guardian_login}</span>
                    ) : null,
                },
                {
                  title: "Mot de passe parent",
                  width: 150,
                  render: (_, r) =>
                    r.guardian_password ? (
                      <span style={{ fontFamily: FONT_MONO, fontSize: 13 }}>{r.guardian_password}</span>
                    ) : null,
                },
                { title: "Message", render: (_, r) => r.message },
              ]}
            />
          </Card>
        </>
      )}
    </div>
  );
};

export default StudentImportPage;
